import { MutableRefObject, RefObject, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { debounce } from 'lodash';
import { Box } from '@mui/material';
import { SiteSearchBar } from '../search';
import { MapFilter } from '../map-filter';

import { useMapFilters } from 'src/contexts/siteMapFilterContext';
import {
  filterNotificationBox,
  mapSearchBarListBox,
  mapSearchBox,
  mapSearchHeaderBox,
  mapSearchHeaderWrapper,
  mapSearchHeaderList,
  mapSearchWrapper,
} from './style';
import { MapNotification } from '../map-notifications/MapNotification';
import { SearchList } from './SearchList';
import {
  MAP_SEARCH_BAR_PLACEHOLDER,
  MAP_SEARCH_BAR_PLACEHOLDER_SHORT,
} from '../map-filter/mapFilterConfig';
import { useResponsive } from 'src/hooks/useResponsive';
import { NewSite } from 'src/types/sites/sites';

export const MapSearch = ({
  mapRef,
  markersRef,
  siteData,
  setSites,
  isLoaded,
  inHeader = false,
}: {
  mapRef: RefObject<L.Map>;
  markersRef: MutableRefObject<Set<L.Marker>>;
  siteData: NewSite[] | [];
  setSites: any;
  isLoaded: boolean;
  inHeader?: boolean;
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const { updateMapSearch } = useMapFilters();
  const isMobile = useResponsive('down', 'sm');

  const [isFocused, setIsFocused] = useState(false);

  const debouncedSetSearchFilter = debounce((value: string) => {
    updateMapSearch(value);
  }, 500);

  // disable zoom when double-click on search
  useEffect(() => {
    if (boxRef?.current) {
      L.DomEvent.disableClickPropagation(boxRef.current);
      L.DomEvent.disableScrollPropagation(boxRef.current);
    }
  }, []);

  const handleBlur = debounce(() => {
    setIsFocused(false);
  }, 200);

  return (
    <Box
      data-testid="map-search-component"
      ref={boxRef}
      sx={inHeader ? mapSearchHeaderBox : mapSearchBox}
    >
      <Box sx={inHeader ? mapSearchHeaderWrapper : mapSearchWrapper}>
        <Box sx={mapSearchBarListBox}>
          <SiteSearchBar
            setSearchValue={debouncedSetSearchFilter}
            setSites={setSites}
            iconRight={<MapFilter insideInput={!inHeader} />}
            iconRightInside={!inHeader}
            onBlur={handleBlur}
            onFocus={() => setIsFocused(true)}
            height="40px"
            placeholder={isMobile ? MAP_SEARCH_BAR_PLACEHOLDER_SHORT : MAP_SEARCH_BAR_PLACEHOLDER}
          />
          {isFocused &&
            (inHeader ? (
              <Box sx={mapSearchHeaderList}>
                <SearchList markersRef={markersRef} mapRef={mapRef} siteData={siteData} />
              </Box>
            ) : (
              <SearchList markersRef={markersRef} mapRef={mapRef} siteData={siteData} />
            ))}
        </Box>
        {!isFocused && (
          <Box sx={filterNotificationBox}>
            {isLoaded && <MapNotification siteData={siteData} />}
          </Box>
        )}
      </Box>
    </Box>
  );
};
